import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { getArtikelBySlug, artikel } from "@/data/magazin";
import { useLanguage } from "@/contexts/LanguageContext";
import { T } from "@/components/T";
import { SEO } from "@/components/SEO";

function formatDate(dateStr, lang) {
  return new Date(dateStr).toLocaleDateString(lang === 'it' ? 'it-IT' : 'de-DE', {
    day: '2-digit', month: 'long', year: 'numeric'
  });
}

function renderMarkdown(text) {
  return text
    .split('\n')
    .map((line, i) => {
      if (line.startsWith('## ')) return <h2 key={i} className="text-2xl font-manrope font-bold text-hb-dark mt-10 mb-4">{line.slice(3)}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className="text-lg font-manrope font-bold text-hb-dark mt-8 mb-3">{line.slice(4)}</h3>;
      if (line.startsWith('**') && line.endsWith('**')) return <strong key={i} className="font-semibold text-hb-dark block mt-4 mb-1">{line.slice(2, -2)}</strong>;
      if (line.startsWith('- ')) return <li key={i} className="ml-4 text-hb-text font-inter text-sm leading-relaxed list-disc">{line.slice(2)}</li>;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-hb-text font-inter text-base leading-relaxed mb-4">{line}</p>;
    });
}

export default function ArtikelDetail() {
  const { slug } = useParams();
  const a = getArtikelBySlug(slug);
  const { lang } = useLanguage();

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!a) {
    return (
      <div className="pt-40 text-center">
        <p className="text-hb-muted"><T de="Artikel nicht gefunden." it="Articolo non trovato." /></p>
        <Link to="/magazin" className="btn-link mt-4 inline-block">
          <T de="Zurück" it="Indietro" />
        </Link>
      </div>
    );
  }

  const related = artikel.filter(r => r.id !== a.id).slice(0, 3);
  const titel = lang === 'it' && a.titel_it ? a.titel_it : a.titel;
  const vorschau = lang === 'it' && a.vorschau_it ? a.vorschau_it : a.vorschau;

  return (
    <div className="pt-20 md:pt-24">
      <SEO
        title={titel}
        description={vorschau}
        canonical={`/magazin/${a.slug}`}
        ogImage={a.bild}
      />
      {/* Hero */}
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <img loading="lazy" src={a.bild} alt={titel} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-hb-dark/80 via-hb-dark/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 container-hb pb-10">
          <Link
            to="/magazin"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs tracking-widest uppercase font-semibold mb-4 transition-colors"
          >
            <ArrowLeft size={12} /> <T de="Magazin" it="Rivista" />
          </Link>
          <span className="label-text text-hb-wood block mb-3">{a.kategorie}</span>
          <h1
            data-testid="artikel-title"
            className="text-3xl sm:text-4xl md:text-5xl font-manrope font-extrabold text-white leading-tight max-w-3xl"
          >
            {titel}
          </h1>
        </div>
      </section>

      {/* Meta */}
      <section className="bg-hb-off-white border-b border-hb-border py-4">
        <div className="container-hb flex flex-wrap gap-6">
          <div className="flex items-center gap-2 text-sm text-hb-text">
            <Calendar size={13} className="text-hb-wood" /> {formatDate(a.datum, lang)}
          </div>
          <div className="flex items-center gap-2 text-sm text-hb-text">
            <Clock size={13} className="text-hb-wood" /> {a.lesezeit} <T de="Lesezeit" it="lettura" />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-gap bg-white" data-testid="artikel-content">
        <div className="container-hb">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <article className="lg:col-span-2 prose-custom">
              <p className="text-xl text-hb-dark font-inter leading-relaxed mb-8 font-light border-l-2 border-hb-wood pl-6">
                {vorschau}
              </p>
              {lang === 'it' && !a.inhalt_it && (
                <div className="bg-hb-off-white border-l-2 border-hb-wood p-4 mb-6 text-sm text-hb-muted font-inter">
                  <T de="" it="Questo articolo è disponibile solo in lingua tedesca." />
                </div>
              )}
              <div>{renderMarkdown(a.inhalt_it && lang === 'it' ? a.inhalt_it : a.inhalt)}</div>
            </article>

            {/* Sidebar */}
            <aside className="space-y-8">
              <div className="bg-hb-off-white p-8">
                <h3 className="text-xs tracking-widest uppercase text-hb-muted font-semibold mb-5">
                  <T de="Projekt geplant?" it="Progetto in programma?" />
                </h3>
                <p className="text-sm text-hb-text font-inter mb-5">
                  <T de="Wir beraten Sie persönlich und kostenlos zu Ihrem Holzbauprojekt."
                     it="Vi consigliamo personalmente e gratuitamente per il vostro progetto in legno." />
                </p>
                <Link
                  to="/kontakt"
                  data-testid="artikel-cta-btn"
                  className="btn-primary text-xs w-full text-center"
                >
                  <T de="Anfrage stellen" it="Richiedi un preventivo" />
                </Link>
              </div>

              <div>
                <h3 className="text-xs tracking-widest uppercase text-hb-muted font-semibold mb-5">
                  <T de="Weitere Artikel" it="Altri articoli" />
                </h3>
                <div className="space-y-4">
                  {related.map(r => (
                    <Link
                      key={r.id}
                      to={`/magazin/${r.slug}`}
                      className="flex gap-3 group"
                    >
                      <div className="w-16 h-16 flex-shrink-0 overflow-hidden">
                        <img
                          src={r.bild}
                          alt={lang === 'it' && r.titel_it ? r.titel_it : r.titel}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div>
                        <p className="text-xs text-hb-wood font-semibold mb-1">{r.kategorie}</p>
                        <p className="text-sm font-manrope font-bold text-hb-dark group-hover:text-hb-wood transition-colors leading-tight">
                          {lang === 'it' && r.titel_it ? r.titel_it : r.titel}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
